import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Search, TrendingUp, Clock, Plus, Music } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { studentSongsDiscoveryService } from '../../services/studentSongsDiscoveryService';
import { useRealtimeSync } from '../../hooks/useRealtimeSync';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

export default function StudentSongsDiscovery() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Content state
  const [continueLearning, setContinueLearning] = useState([]);
  const [popularSongs, setPopularSongs] = useState([]);
  const [recentlyUpdated, setRecentlyUpdated] = useState([]);
  const [recentlyAdded, setRecentlyAdded] = useState([]);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // ✅ Real-time sync for songs - students see updates instantly
  useRealtimeSync('songs', {
    onInsert: (newSong) => {
      console.log('New song available:', newSong);
      // Show notification and refetch
      fetchSongs();
    },
    onUpdate: (updatedSong) => {
      console.log('Song content updated:', updatedSong);
      // Update the song in the current view
      setSongs(prevSongs => 
        prevSongs?.map(song => 
          song?.id === updatedSong?.id ? { ...song, ...updatedSong } : song
        )
      );
      // Optional: Show a toast notification
      // toast.info(`Song "${updatedSong.title}" has been updated`);
    },
    onDelete: (deletedSong) => {
      console.log('Song removed:', deletedSong);
      // Remove from student's view
      setSongs(prevSongs => 
        prevSongs?.filter(song => song?.id !== deletedSong?.id)
      );
    }
  });

  const fetchSongs = async () => {
    try {
      setLoading(true);
      setError('');

      const [continueData, popularData, updatedData, addedData] = await Promise.all([
        studentSongsDiscoveryService?.getContinueLearningSongs(user?.id, 12),
        studentSongsDiscoveryService?.getPopularSongs(12),
        studentSongsDiscoveryService?.getRecentlyUpdatedSongs(12),
        studentSongsDiscoveryService?.getRecentlyAddedSongs(12)
      ]);

      setContinueLearning(continueData);
      setPopularSongs(popularData);
      setRecentlyUpdated(updatedData);
      setRecentlyAdded(addedData);
    } catch (err) {
      console.error('Error loading songs:', err);
      setError(err?.message || 'Failed to load songs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchSongs();
    }
  }, [user]);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery?.trim()) {
      setSearchResults(null);
      return;
    }

    try {
      setSearchLoading(true);
      const results = await studentSongsDiscoveryService?.searchSongs(searchQuery);
      setSearchResults(results);
    } catch (err) {
      console.error('Error searching:', err);
      setError(err?.message || 'Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  const navigateToSong = (songId) => {
    navigate(`/song-progress-page?id=${songId}`);
  };

  const SongCard = ({ song }) => (
    <div
      onClick={() => navigateToSong(song?.id)}
      className="flex-shrink-0 w-48 cursor-pointer hover:scale-105 transition-transform"
    >
      <div className="bg-white/5 rounded-lg p-4 mb-2">
        <Music className="w-12 h-12 text-purple-400 mx-auto mb-2" />
        <p className="text-white text-sm font-medium text-center truncate">{song?.title}</p>
        <p className="text-purple-300 text-xs text-center truncate">{song?.musicalTitle}</p>
        {song?.actNumber && (
          <p className="text-white/50 text-xs text-center mt-1">Act {song?.actNumber}</p>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Songs - RoleCall Student Discovery</title>
        <meta name="description" content="Discover and explore musical theater songs with comprehensive learning pathways" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-red-500/10 border border-red-500 rounded-xl p-4">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}

          {/* Search Bar */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  placeholder="Search songs..."
                  className="w-full bg-white/5 border border-white/20 rounded-xl pl-10 pr-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-purple-400"
                />
              </div>
              <button
                type="submit"
                disabled={searchLoading}
                className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
              >
                {searchLoading ? 'Searching...' : 'Search'}
              </button>
            </form>

            {/* Search Results */}
            {searchResults && (
              <div className="mt-6">
                <h3 className="text-white font-semibold mb-3">Search Results ({searchResults?.length})</h3>
                {searchResults?.length > 0 ? (
                  <div className="space-y-2">
                    {searchResults?.map((song) => (
                      <div
                        key={song?.id}
                        onClick={() => navigateToSong(song?.id)}
                        className="flex gap-3 p-3 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 transition-colors"
                      >
                        <Music className="w-10 h-10 text-purple-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{song?.title}</p>
                          <p className="text-purple-300 text-sm truncate">{song?.musicalTitle}</p>
                          {song?.actNumber && (
                            <p className="text-white/50 text-xs">Act {song?.actNumber}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/70">No songs found matching "{searchQuery}"</p>
                )}
              </div>
            )}
          </div>

          {/* Continue Learning */}
          {continueLearning?.length > 0 && (
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Clock className="w-6 h-6 text-blue-400" />
                Continue Learning
              </h2>
              <div className="overflow-x-auto">
                <div className="flex gap-4 pb-4">
                  {continueLearning?.map((song) => (
                    <SongCard key={song?.id} song={song} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Popular Songs */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-yellow-400" />
              Popular Songs
            </h2>
            <div className="overflow-x-auto">
              <div className="flex gap-4 pb-4">
                {popularSongs?.map((song) => (
                  <SongCard key={song?.id} song={song} />
                ))}
              </div>
            </div>
          </div>

          {/* Recently Updated Songs */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Clock className="w-6 h-6 text-green-400" />
              Recently Updated Songs
            </h2>
            <div className="overflow-x-auto">
              <div className="flex gap-4 pb-4">
                {recentlyUpdated?.map((song) => (
                  <SongCard key={song?.id} song={song} />
                ))}
              </div>
            </div>
          </div>

          {/* Recently Added Songs */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Plus className="w-6 h-6 text-purple-400" />
              Recently Added Songs
            </h2>
            <div className="overflow-x-auto">
              <div className="flex gap-4 pb-4">
                {recentlyAdded?.map((song) => (
                  <SongCard key={song?.id} song={song} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}